import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import AuthSplit from '../components/auth/AuthSplit';

const Login: React.FC = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from || '/dashboard';

  useEffect(() => {
    if (!loading && isAuthenticated) navigate(from, { replace: true });
  }, [from, isAuthenticated, loading, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in. Check your email and password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplit
      eyebrow="Student sign in"
      title="Welcome back to LSEM."
      lede="Your programme, lessons, assessments and certificate record are waiting in one place."
      notes={
        <div className="space-y-2.5">
          <p>Paid programme access is attached to the email you used at checkout.</p>
          <p>Free pathways open as soon as your student account is confirmed.</p>
          <p>
            Just paid? Check your inbox for the access link we sent to your checkout email. You can also use{' '}
            <Link to="/forgot-password" className="auth-link">
              account recovery
            </Link>{' '}
            to request a fresh sign-in link.
          </p>
        </div>
      }
      footer={
        <Link
          to="/signup"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 transition-colors hover:text-blue-800"
        >
          New to LSEM? Create your student account
          <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
        </Link>
      }
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-950">Sign in to your account</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-emerald-700">
            <LockKeyhole className="h-3 w-3" strokeWidth={1.8} /> Secure
          </span>
        </div>

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] leading-5 text-red-700" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-5">
          <div>
            <label htmlFor="login-email" className="auth-label">Email address</label>
            <input
              id="login-email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              className="auth-field"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor="login-password" className="auth-label">Password</label>
              <Link
                to="/forgot-password"
                className="mb-2 text-[11px] font-semibold text-blue-700 transition-colors hover:text-blue-800"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                className="auth-field pr-12"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition-colors hover:text-slate-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.7} /> : <Eye className="h-4 w-4" strokeWidth={1.7} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={busy} className="auth-btn-primary">
            {busy ? 'Signing in…' : 'Sign in to LSEM'}
          </button>
        </form>

        <p className="mt-5 flex items-start gap-2 rounded-xl bg-slate-50 px-3.5 py-3 text-[11.5px] leading-5 text-slate-500">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-emerald-600" strokeWidth={1.7} />
          <span>
            New student account? Confirm your email first, then sign in. Your free programme is added automatically
            when you confirm.
          </span>
        </p>
      </div>
    </AuthSplit>
  );
};

export default Login;
